import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { CalendarCheck, CheckCircle2, Clock, MapPin, Monitor, Users, XCircle } from 'lucide-react'
import { portalApi, type PortalSessionBookingDto, type PortalSessionDto } from '@/api/portal'
import { Button } from '@/components/ui/button'
import { usePortalDates } from '@/hooks/useDates'
import { usePortalMoney } from '@/hooks/useMoney'
import { usePortalPermissions } from '@/hooks/usePortalPermissions'
import { guardPreview } from '@/lib/previewMode'
import BookStaffModal from './BookStaffModal'

const STATUS_BADGE: Record<string, { label: string; cls: string }> = {
  Confirmed: { label: 'Confirmed', cls: 'bg-green-100 text-green-700' },
  AwaitingPayment: { label: 'Awaiting payment', cls: 'bg-amber-100 text-amber-700' },
  Cancelled: { label: 'Cancelled', cls: 'bg-red-100 text-red-600' },
}

export default function SessionsPage() {
  const { formatDate, formatTime } = usePortalDates()
  const { fmt } = usePortalMoney()
  const permissions = usePortalPermissions()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const [courseFilter, setCourseFilter] = useState('')
  const [booking, setBooking] = useState<PortalSessionDto | null>(null)

  const paymentResult = searchParams.get('payment')

  const { data, isLoading } = useQuery({
    queryKey: ['portal-sessions'],
    queryFn: () => portalApi.getSessions().then(r => r.data),
  })
  const { data: myBookings = [] } = useQuery({
    queryKey: ['portal-session-bookings'],
    queryFn: () => portalApi.getSessionBookings().then(r => r.data),
  })
  const { data: staff = [] } = useQuery({
    queryKey: ['portal-staff'],
    queryFn: () => portalApi.getStaff().then(r => r.data),
  })

  const sessions = data?.sessions ?? []
  const courses = useMemo(
    () => Array.from(new Map(sessions.map(s => [s.courseId, s.courseName])).entries()).sort((a, b) => a[1].localeCompare(b[1])),
    [sessions],
  )
  const visible = sessions.filter(s => !courseFilter || s.courseId === courseFilter)

  const upcomingBookings = myBookings.filter(b => new Date(b.startsAt) >= new Date() && b.status !== 'Cancelled')
  const otherBookings = myBookings.filter(b => !upcomingBookings.includes(b))

  const dateLine = (s: { startsAt: string; endsAt: string | null }) =>
    `${formatDate(s.startsAt, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}, ${formatTime(s.startsAt)}${s.endsAt ? `–${formatTime(s.endsAt)}` : ''}`

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['portal-sessions'] })
    queryClient.invalidateQueries({ queryKey: ['portal-session-bookings'] })
  }

  return (
    <div className="max-w-4xl space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Open sessions</h1>
        <p className="text-sm text-gray-500 mt-1">
          Book your own staff onto our upcoming sessions. Places are confirmed straight away, and the training they complete counts towards your compliance.
        </p>
      </div>

      {paymentResult === 'success' && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800 flex items-start gap-2">
          <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
          <span>Payment received. Your places are confirmed, and a confirmation email is on its way. It can take a moment to show below.</span>
          <button className="ml-auto text-green-700 underline text-xs" onClick={() => { setSearchParams({}); refresh() }}>Dismiss</button>
        </div>
      )}
      {paymentResult === 'cancelled' && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-start gap-2">
          <Clock size={16} className="mt-0.5 shrink-0" />
          <span>You didn't finish paying, so your places are only held for a short time. You can finish paying below, or book again.</span>
          <button className="ml-auto text-amber-800 underline text-xs" onClick={() => { setSearchParams({}); refresh() }}>Dismiss</button>
        </div>
      )}

      {/* Upcoming sessions */}
      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-gray-900">Upcoming sessions</h2>
          {courses.length > 1 && (
            <select
              value={courseFilter}
              onChange={e => setCourseFilter(e.target.value)}
              className="h-9 rounded-md border border-input bg-transparent px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              aria-label="Filter by course"
            >
              <option value="">All courses</option>
              {courses.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          )}
        </div>

        {isLoading ? (
          <div className="flex justify-center py-10"><div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : visible.length === 0 ? (
          <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
            No open sessions to book right now.
            {permissions.bookingRequests && <> <Link to="/portal/bookings" className="text-indigo-600 hover:underline">Request a booking</Link> and we'll arrange a date with you.</>}
          </div>
        ) : (
          <div className="space-y-3">
            {visible.map(s => {
              const full = s.availablePlaces === 0
              return (
                <div key={s.id} className="rounded-xl border border-gray-200 bg-white p-5 flex flex-wrap items-start justify-between gap-4">
                  <div className="space-y-1.5 min-w-0">
                    <p className="font-semibold text-gray-900">{s.courseName}</p>
                    <p className="text-sm text-gray-600">{dateLine(s)}</p>
                    {s.additionalDays.length > 0 && (
                      <p className="text-xs text-gray-400">
                        Also {s.additionalDays.map(d => formatDate(d.startsAt, { day: 'numeric', month: 'short' })).join(', ')}
                      </p>
                    )}
                    <p className="text-xs text-gray-500 flex items-center gap-1.5">
                      {s.deliveryMethod === 'Online' ? <><Monitor size={12} /> Online</> : <><MapPin size={12} /> {[s.venueName, s.venueCity].filter(Boolean).join(', ') || 'Venue to be confirmed'}</>}
                    </p>
                    <p className="text-xs text-gray-500 flex items-center gap-3">
                      <span className="flex items-center gap-1"><Users size={12} /> {full ? 'Full' : `${s.availablePlaces} of ${s.totalPlaces} places left`}</span>
                      <span className="font-medium text-gray-700">{s.pricePerPerson ? `${fmt(s.pricePerPerson)} per person` : 'Free'}</span>
                    </p>
                    {s.bookedStaffIds.length > 0 && (
                      <p className="text-xs text-green-700 flex items-center gap-1"><CheckCircle2 size={12} /> {s.bookedStaffIds.length} of your team already booked</p>
                    )}
                  </div>
                  <Button disabled={full} onClick={() => { if (!guardPreview()) setBooking(s) }}>
                    <CalendarCheck size={14} className="mr-1.5" /> {full ? 'Full' : 'Book staff'}
                  </Button>
                </div>
              )
            })}
          </div>
        )}

        {visible.length > 0 && permissions.bookingRequests && (
          <p className="text-xs text-gray-400">Can't find a date that suits? <Link to="/portal/bookings" className="text-indigo-600 hover:underline">Request a booking</Link> instead.</p>
        )}
      </section>

      {/* Their bookings */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-gray-900">Your session bookings</h2>
        {myBookings.length === 0 ? (
          <p className="text-sm text-gray-400">You haven't booked onto an open session yet.</p>
        ) : (
          <div className="space-y-3">
            {[...upcomingBookings, ...otherBookings].map(b => <BookingCard key={b.bookingId} b={b} dateLine={dateLine({ startsAt: b.startsAt, endsAt: null })} fmt={fmt} />)}
          </div>
        )}
      </section>

      {booking && (
        <BookStaffModal
          session={booking}
          staff={staff}
          requiresPayment={data?.requiresPaymentToBook ?? false}
          dateLine={dateLine(booking)}
          onClose={() => setBooking(null)}
          onBooked={refresh}
        />
      )}
    </div>
  )
}

function BookingCard({ b, dateLine, fmt }: { b: PortalSessionBookingDto; dateLine: string; fmt: (n: number) => string }) {
  const badge = STATUS_BADGE[b.status] ?? { label: b.status, cls: 'bg-gray-100 text-gray-600' }
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-2">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-semibold text-gray-900">{b.courseName}</p>
          <p className="text-sm text-gray-600">{dateLine}</p>
          {b.venueName && b.deliveryMethod !== 'Online' && <p className="text-xs text-gray-400">{b.venueName}</p>}
        </div>
        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${badge.cls}`}>{badge.label}</span>
      </div>
      <p className="text-sm text-gray-600">
        {b.attendees.map(a => a.name).join(', ')}
      </p>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
        <span>{b.places} place{b.places === 1 ? '' : 's'}</span>
        {b.total != null && b.total > 0 && <span>{fmt(b.total)} {b.isPaid ? <span className="text-green-700 font-medium">· Paid</span> : <span className="text-amber-700 font-medium">· Not yet paid</span>}</span>}
        {b.status === 'AwaitingPayment' && b.paymentPath && (
          <a href={b.paymentPath} className="text-indigo-600 hover:underline font-medium">Finish paying</a>
        )}
        {b.status === 'Cancelled' && <span className="flex items-center gap-1 text-red-500"><XCircle size={12} /> Cancelled by the training company. Contact them to rearrange.</span>}
      </div>
    </div>
  )
}
